import { useState, useEffect, useMemo, useCallback } from "react";
import { getOrderIdsByUser, getProductById, getDamageHistory, addDamage } from "../api/productApi";
import { calculateDamageAmountAuto } from "../utils/damageCalculator";

export default function useDamageForm({ book, token, initialMemberId, initialOrderId, onSaveSuccess }) {
    const [memberId, setMemberId] = useState(initialMemberId ? String(initialMemberId) : "");
    const [orderId, setOrderId] = useState(initialOrderId ? String(initialOrderId) : "");
    const [reason, setReason] = useState("");
    const [severity, setSeverity] = useState("");
    const [copyId, setCopyId] = useState("");
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [amount, setAmount] = useState("");
    const [remark, setRemark] = useState("");
    const [sendEmail, setSendEmail] = useState(false);
    const [imagesBase64, setImagesBase64] = useState([]);

    const [orderIds, setOrderIds] = useState([]);
    const [productCopies, setProductCopies] = useState([]);
    const [damageHistory, setDamageHistory] = useState([]);

    const [ordersLoading, setOrdersLoading] = useState(false);
    const [copiesLoading, setCopiesLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    // Reset form states
    const resetForm = useCallback(() => {
        setReason("");
        setSeverity("");
        setCopyId("");
        setAmount("");
        setRemark("");
        setSendEmail(false);
        setImagesBase64([]);
        setError("");
        setSuccessMsg("");
    }, []);

    // Set initial values on open/change
    useEffect(() => {
        setError("");
        setSuccessMsg("");
        resetForm();

        const initialOrder =
            book?.added_order_id ||
            book?.order_id ||
            book?.activeOrder?.added_order_id ||
            book?.activeOrder?.post_order_id ||
            book?.activeOrder?.id ||
            book?.post_order_id ||
            initialOrderId;

        setOrderId(initialOrder ? String(initialOrder) : "");
        setMemberId(initialMemberId ? String(initialMemberId) : "");
        setDate(new Date().toISOString().slice(0, 10));

        const initialCopyId = book?.copy_post_id || book?.copy_id || "";
        setCopyId(initialCopyId ? String(initialCopyId) : "");
        if (book?.initialReason) {
            setReason(book.initialReason);
        }
    }, [book, initialMemberId, initialOrderId, resetForm]);

    // Fetch orders whenever member changes
    useEffect(() => {
        if (!memberId || !token) {
            setOrderIds([]);
            return;
        }

        let ignore = false;
        async function fetchOrders() {
            setOrdersLoading(true);
            try {
                const res = await getOrderIdsByUser({ token, userId: memberId });
                if (ignore) return;

                if (Array.isArray(res)) {
                    setOrderIds(res);
                } else if (res?.success) {
                    setOrderIds(res.order_ids || res.data || []);
                } else if (res?.order_ids) {
                    setOrderIds(res.order_ids);
                } else if (res?.data) {
                    setOrderIds(res.data);
                } else {
                    setOrderIds([]);
                }
            } catch (e) {
                if (!ignore) setOrderIds([]);
            } finally {
                if (!ignore) setOrdersLoading(false);
            }
        }
        fetchOrders();

        return () => {
            ignore = true;
        };
    }, [memberId, token]);

    // Fetch product copies when book changes
    useEffect(() => {
        if (!book?.id || !token) {
            setProductCopies([]);
            return;
        }

        let ignore = false;
        async function loadProduct() {
            try {
                setCopiesLoading(true);
                const res = await getProductById({ token, id: book.id });
                if (!ignore && res) {
                    const c = res.copys || res.data?.copys;
                    setProductCopies(Array.isArray(c) ? c : []);
                }
            } catch (e) {
                if (!ignore) setProductCopies([]);
            } finally {
                if (!ignore) setCopiesLoading(false);
            }
        }

        loadProduct();
        return () => {
            ignore = true;
        };
    }, [book?.id, token]);

    // Load damage history for the selected order
    const loadDamageHistory = useCallback(async (selectedOrder) => {
        if (!selectedOrder || !token) {
            setDamageHistory([]);
            return;
        }

        setHistoryLoading(true);
        setError("");
        try {
            const res = await getDamageHistory({ token, addedOrderId: selectedOrder });
            if (res?.success === false) {
                setError(res?.message || "Failed to load damage history");
                setDamageHistory([]);
                return;
            }
            setDamageHistory(res?.data || []);
        } catch (e) {
            setError("Failed to load damage history");
            setDamageHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    }, [token]);

    // Refresh history whenever order changes
    useEffect(() => {
        if (!orderId) {
            setDamageHistory([]);
            return;
        }
        loadDamageHistory(orderId);
    }, [orderId, loadDamageHistory]);

    // Auto-calculate amount when reason/severity/copy changes
    const selectedCopyObj = useMemo(() => {
        if (!copyId) return null;
        return productCopies.find((c) => String(c.copy_id) === String(copyId));
    }, [productCopies, copyId]);

    useEffect(() => {
        if (reason === "Other Damages") {
            setAmount("");
            return;
        }
        const price = selectedCopyObj?.copy_price || 0;
        const calculatedAmt = calculateDamageAmountAuto({
            productPrice: price,
            reason: reason,
            severity: Number(severity)
        });
        if (calculatedAmt > 0) {
            setAmount(String(calculatedAmt));
        } else {
            setAmount("");
        }
    }, [reason, severity, selectedCopyObj]);

    const copyOptions = useMemo(() => {
        const base = [{ label: "Select Copy", value: "" }];
        if (!Array.isArray(productCopies) || !productCopies.length) return base;
        return [
            ...base,
            ...productCopies.map((c) => ({
                label: `Copy Id ${c.copy_id}`,
                value: c.copy_id,
            })),
        ];
    }, [productCopies]);

    // Handle multiple image uploads & convert to Base64
    const handleImageUpload = useCallback(async (filesList) => {
        const files = Array.from(filesList);
        if (!files.length) return;

        const toBase64 = (file) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result?.split(",")?.[1] || "");
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });

        try {
            const converted = await Promise.all(files.map(toBase64));
            setImagesBase64((prev) => [...prev, ...converted.filter(Boolean)]);
        } catch (err) {
            console.error("Image convert failed", err);
        }
    }, []);

    // Handle submit (save damage)
    const handleSaveDamage = useCallback(async () => {
        setError("");
        setSuccessMsg("");

        if (!book?.id) return setError("Book missing");
        if (!memberId) return setError("Please select Member");
        if (!reason) return setError("Please select Reason");
        if (!severity) return setError("Please select Severity");
        if (!date) return setError("Please select Date");
        if (!orderId) return setError("Please select an Order ID.");

        try {
            setSaving(true);
            const payload = {
                post_order_id: Number(orderId),
                email: Boolean(sendEmail),
                data: {
                    date,
                    added_order_id: Number(orderId),
                    remark: remark || "",
                    type: reason,
                    severity: Number(severity),
                    sq_images_base64_data: imagesBase64,
                    copys: copyId
                        ? (copyOptions.find((o) => String(o.value) === String(copyId))?.label || "-")
                        : "-",
                    copy_post_id: copyId ? Number(selectedCopyObj?.copy_post_id || copyId) : null,
                    sq_copy_id: copyId ? Number(copyId) : null,
                    sq_product_id: Number(book.id),
                    amt: Number(amount || 0),
                },
            };

            const res = await addDamage({ token, payload });
            if (res?.success === false) {
                setError(res?.message || "Failed to save damage");
                return;
            }

            setSuccessMsg("Damage saved successfully ✅");
            if (onSaveSuccess) onSaveSuccess(payload);

            // Reload history
            await loadDamageHistory(orderId);

            // Reset part of the form
            setReason("");
            setSeverity("");
            setCopyId("");
            setAmount("");
            setRemark("");
            setSendEmail(false);
            setImagesBase64([]);
        } catch (e) {
            setError(e.message || "Failed to save damage");
        } finally {
            setSaving(false);
        }
    }, [
        book?.id,
        memberId,
        reason,
        severity,
        date,
        orderId,
        sendEmail,
        remark,
        imagesBase64,
        copyId,
        copyOptions,
        amount,
        token,
        onSaveSuccess,
        loadDamageHistory,
        selectedCopyObj,
    ]);

    return {
        memberId,
        setMemberId,
        orderId,
        setOrderId,
        reason,
        setReason,
        severity,
        setSeverity,
        copyId,
        setCopyId,
        date,
        setDate,
        amount,
        setAmount,
        remark,
        setRemark,
        sendEmail,
        setSendEmail,
        imagesBase64,
        setImagesBase64,
        orderIds,
        productCopies,
        damageHistory,
        ordersLoading,
        copiesLoading,
        historyLoading,
        saving,
        error,
        setError,
        successMsg,
        copyOptions,
        selectedCopyObj,
        handleImageUpload,
        handleSaveDamage,
        loadDamageHistory,
        resetForm,
    };
}
